import React, { useMemo, useState, useEffect, useCallback, useRef } from 'react'
import PropTypes from 'prop-types'
import {
  HeroBannerContainer,
  HeroBannerContent,
  HeroBannerTitle,
  HeroBannerSubtitle,
  HeroBannerButton,
  CarouselWrapper,
  CarouselTrack,
  CarouselSlide,
  DotsContainer,
  Dot,
} from './HeroBanner.styles'
import imageCache from '../../utils/imageCache'
import logger from '../../utils/logger'

const IMAGE_LOAD_TIMEOUT = 5000
const AUTO_ADVANCE_INTERVAL = 5000
const SWIPE_THRESHOLD = 50

// Normaliza e força HTTPS nas URLs do S3
const normalizeS3Url = (url) => {
  if (!url) return url

  // Converte de virtual-hosted-style para path-style que funciona com HTTPS
  // De: http://arquivos.academiarafaeltoro.com.br.s3.sa-east-1.amazonaws.com/path
  // Para: https://s3.sa-east-1.amazonaws.com/arquivos.academiarafaeltoro.com.br/path
  const s3Pattern = /^https?:\/\/(.+?)\.s3\.([^/]+)\.amazonaws\.com\/(.+)$/
  const match = url.match(s3Pattern)

  if (match) {
    const [, bucketName, region, path] = match
    return `https://s3.${region}.amazonaws.com/${bucketName}/${path}`
  }

  // Se não corresponder ao padrão, apenas força HTTPS
  return url.replace(/^http:/, 'https:')
}

// Componente interno para renderizar um único slide do banner
const BannerSlide = ({ banner, isCarouselSlide }) => {
  const [imageLoaded, setImageLoaded] = useState(false)
  const [imageError, setImageError] = useState(false)
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768)

  const buttonText = useMemo(() => {
    return banner?.cta_text || banner?.button_text
  }, [banner])

  const desktopImageUrl = normalizeS3Url(banner?.desktop_image_url)
  const mobileImageUrl = normalizeS3Url(banner?.mobile_image_url)

  // Listener de resize para detecção mobile reativa
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768)
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Memoização do cálculo de URLs primária e secundária
  const imageUrls = useMemo(() => ({
    primary: isMobile ? (mobileImageUrl || desktopImageUrl) : desktopImageUrl,
    secondary: isMobile ? desktopImageUrl : mobileImageUrl
  }), [isMobile, desktopImageUrl, mobileImageUrl])

  // Preload das imagens com cache
  useEffect(() => {
    if (!desktopImageUrl && !mobileImageUrl) {
      setImageLoaded(true)
      return
    }

    const { primary: primaryImageUrl, secondary: secondaryImageUrl } = imageUrls

    if (!primaryImageUrl) {
      setImageLoaded(true)
      return
    }

    // Verifica se a imagem primária já está em cache
    const cachedResult = imageCache.isInCache(primaryImageUrl)
    if (cachedResult !== null) {
      setImageLoaded(true)
      setImageError(!cachedResult)

      // Preload da imagem secundária em background (se existir)
      if (secondaryImageUrl && imageCache.isInCache(secondaryImageUrl) === null) {
        imageCache.loadImage(secondaryImageUrl)
      }
      return
    }

    setImageLoaded(false)
    setImageError(false)

    let isMounted = true

    // Timeout de segurança - se a imagem não carregar, mostra o fallback
    const timeout = setTimeout(() => {
      if (!isMounted) return

      logger.warn('Timeout ao carregar imagem do hero banner:', primaryImageUrl)
      setImageLoaded(true)
      setImageError(true)
    }, IMAGE_LOAD_TIMEOUT)

    // Carrega a imagem primária usando o cache
    imageCache.loadImage(primaryImageUrl).then((success) => {
      if (!isMounted) return

      clearTimeout(timeout)
      setImageLoaded(true)
      setImageError(!success)

      // Preload da imagem secundária em background (se existir e primária carregou)
      if (success && secondaryImageUrl && imageCache.isInCache(secondaryImageUrl) === null) {
        imageCache.loadImage(secondaryImageUrl)
      }
    })

    return () => {
      isMounted = false
      clearTimeout(timeout)
    }
  }, [desktopImageUrl, mobileImageUrl, imageUrls])

  const handleClick = () => {
    const url = banner.cta_url || banner.link_url
    if (url) {
      try {
        window.open(url, '_blank', 'noopener,noreferrer')
      } catch (error) {
        logger.warn('Falha ao abrir URL do banner em nova aba, usando mesma aba:', error)
        // Fallback para navegação na mesma aba
        window.location.href = url
      }
    }
  }

  return (
    <HeroBannerContainer
      desktopImage={imageLoaded && !imageError ? desktopImageUrl : null}
      mobileImage={imageLoaded && !imageError ? mobileImageUrl : null}
      isLoading={!imageLoaded}
      isCarouselSlide={isCarouselSlide}
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          handleClick()
        }
      }}
      aria-busy={!imageLoaded}
      aria-label={!imageLoaded ? 'Carregando banner promocional' : banner.title || 'Banner promocional'}
    >
      <HeroBannerContent>
        <HeroBannerTitle>{banner.title}</HeroBannerTitle>
        <HeroBannerSubtitle>{banner.subtitle}</HeroBannerSubtitle>
        {buttonText && (
          <HeroBannerButton>
            {buttonText}
            <span className="arrow">→</span>
          </HeroBannerButton>
        )}
      </HeroBannerContent>
    </HeroBannerContainer>
  )
}

BannerSlide.propTypes = {
  banner: PropTypes.shape({
    title: PropTypes.string,
    subtitle: PropTypes.string,
    cta_text: PropTypes.string,
    button_text: PropTypes.string,
    cta_url: PropTypes.string,
    link_url: PropTypes.string,
    desktop_image_url: PropTypes.string,
    mobile_image_url: PropTypes.string,
  }).isRequired,
  isCarouselSlide: PropTypes.bool,
}

BannerSlide.defaultProps = {
  isCarouselSlide: false,
}

// Distância mínima (px) de movimento para considerar que houve arraste (e não um clique)
const DRAG_CLICK_THRESHOLD = 8

// Componente principal: renderiza carrossel quando há múltiplos banners
const HeroBanner = ({ banners }) => {
  const [currentIndex, setCurrentIndex] = useState(0)
  const [dragOffset, setDragOffset] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const dragStartX = useRef(null)
  const didDragRef = useRef(false)
  const isPausedRef = useRef(false)

  const totalSlides = banners?.length || 0

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % totalSlides)
  }, [totalSlides])

  const goToPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides)
  }, [totalSlides])

  const goToSlide = useCallback((index) => {
    setCurrentIndex(index)
  }, [])

  // Auto-advance: avança automaticamente a cada intervalo, pausando em hover/toque
  useEffect(() => {
    if (totalSlides <= 1) return

    const interval = setInterval(() => {
      if (!isPausedRef.current) {
        setCurrentIndex((prev) => (prev + 1) % totalSlides)
      }
    }, AUTO_ADVANCE_INTERVAL)

    return () => clearInterval(interval)
  }, [totalSlides])

  const handleMouseEnter = useCallback(() => { isPausedRef.current = true }, [])
  const handleMouseLeave = useCallback(() => {
    // Encerra também qualquer arraste em andamento com o mouse fora do componente
    if (dragStartX.current !== null) {
      dragStartX.current = null
      setIsDragging(false)
      setDragOffset(0)
    }
    isPausedRef.current = false
  }, [])

  // --- Lógica unificada de arraste (funciona para toque e mouse) ---
  const startDrag = useCallback((clientX) => {
    dragStartX.current = clientX
    didDragRef.current = false
    isPausedRef.current = true
    setIsDragging(true)
    setDragOffset(0)
  }, [])

  const moveDrag = useCallback((clientX) => {
    if (dragStartX.current === null) return
    const delta = clientX - dragStartX.current
    if (Math.abs(delta) > DRAG_CLICK_THRESHOLD) {
      didDragRef.current = true
    }
    setDragOffset(delta)
  }, [])

  const endDrag = useCallback((clientX) => {
    if (dragStartX.current === null) return

    const diff = dragStartX.current - clientX

    if (Math.abs(diff) >= SWIPE_THRESHOLD) {
      if (diff > 0) {
        goToNext()
      } else {
        goToPrev()
      }
    }

    dragStartX.current = null
    setIsDragging(false)
    setDragOffset(0)
    isPausedRef.current = false
  }, [goToNext, goToPrev])

  // Handlers de toque
  const handleTouchStart = useCallback((e) => { startDrag(e.touches[0].clientX) }, [startDrag])
  const handleTouchMove = useCallback((e) => { moveDrag(e.touches[0].clientX) }, [moveDrag])
  const handleTouchEnd = useCallback((e) => { endDrag(e.changedTouches[0].clientX) }, [endDrag])

  // Handlers de mouse (swipe/arraste também no desktop)
  const handleMouseDown = useCallback((e) => { startDrag(e.clientX) }, [startDrag])
  const handleMouseMove = useCallback((e) => {
    if (dragStartX.current === null) return
    moveDrag(e.clientX)
  }, [moveDrag])
  const handleMouseUp = useCallback((e) => { endDrag(e.clientX) }, [endDrag])

  // Impede que um arraste dispare o clique (redirecionamento) do banner
  const handleClickCapture = useCallback((e) => {
    if (didDragRef.current) {
      e.preventDefault()
      e.stopPropagation()
      didDragRef.current = false
    }
  }, [])

  if (!banners || banners.length === 0) {
    return null
  }

  // Banner único: renderiza sem estrutura de carrossel
  if (banners.length === 1) {
    return <BannerSlide banner={banners[0]} />
  }

  return (
    <CarouselWrapper
      aria-label="Carrossel de banners"
      aria-roledescription="carousel"
      isDragging={isDragging}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onClickCapture={handleClickCapture}
    >
      <CarouselTrack
        isDragging={isDragging}
        style={{ transform: `translateX(calc(-${currentIndex * 100}% + ${dragOffset}px))` }}
      >
        {banners.map((banner, index) => (
          <CarouselSlide
            key={banner.id}
            role="group"
            aria-roledescription="slide"
            aria-label={`Banner ${index + 1} de ${totalSlides}`}
          >
            <BannerSlide banner={banner} isCarouselSlide />
          </CarouselSlide>
        ))}
      </CarouselTrack>
      <DotsContainer aria-label="Indicadores de posição do carrossel">
        {banners.map((banner, index) => (
          <Dot
            key={banner.id}
            isActive={index === currentIndex}
            onClick={() => goToSlide(index)}
            aria-label={`Ir para banner ${index + 1}`}
            aria-current={index === currentIndex ? 'true' : undefined}
          />
        ))}
      </DotsContainer>
    </CarouselWrapper>
  )
}

HeroBanner.propTypes = {
  banners: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      title: PropTypes.string,
      subtitle: PropTypes.string,
      cta_text: PropTypes.string,
      button_text: PropTypes.string,
      cta_url: PropTypes.string,
      link_url: PropTypes.string,
      desktop_image_url: PropTypes.string,
      mobile_image_url: PropTypes.string,
    })
  ),
}

HeroBanner.defaultProps = {
  banners: [],
}

export default HeroBanner
